import { useCallback, useEffect, useState } from 'react';
import { type ActivityQualification, type BoardActivity } from '@lemontaigu/staff';
import { useBoard, useBoardPresence } from '@lemontaigu/staff/react';
import { fmtDayLabel } from './logic.js';

// Self quick-edit from the Semaine / Mois matrix (operator request 2026-08-04):
// tapping MY OWN column on a day opens this panel to declare activities or set
// repos for that day, without navigating to the Jour tab and stepping the date
// there.
//
// Deliberately NOT reception's EmployeeQuickEdit: that one writes
// status='assigned', source='operator' rows, which only `bap_admin_all` admits.
// This performs exactly the self-tap writes the Jour tab already performs
// (ADR-0011 §5) — own row, source='tap', qualification-gated on insert — so it
// adds a surface for a gesture the employee could already make, and no new
// authority. RLS remains the gate: the predicate that opened this panel is an
// affordance, not a permission.
//
// A teammate's column is not editable here. That needs the senior capability
// and its migration (decided 2026-08-04, not yet built) — until then the
// caller's predicate simply answers false for every id but the signed-in one.

export function SelfQuickEdit({
  day,
  employeeId,
  activities,
  qualifications,
  onClose,
  onChanged,
}: {
  day: string;
  employeeId: string;
  activities: BoardActivity[];
  qualifications: ActivityQualification[];
  onClose: () => void;
  /** Fired after a successful write so the matrix behind can re-read. */
  onChanged: () => void;
}) {
  const board = useBoard();
  const boardPresence = useBoardPresence();
  const [absent, setAbsent] = useState(false);
  const [declaredIds, setDeclaredIds] = useState<Set<number>>(new Set());
  const [loaded, setLoaded] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [err, setErr] = useState('');

  // Only what I'm qualified for, active — the same list the Jour tab offers,
  // and the same list the insert policy will accept.
  const myActivities = activities.filter(
    (a) => a.active && qualifications.some((q) => q.employeeId === employeeId && q.activityId === a.id),
  );

  const load = useCallback(async () => {
    setLoaded(false);
    setErr('');
    try {
      const [ab, pr] = await Promise.all([board.absences(day, day), board.presence(day, day)]);
      setAbsent(ab.some((r) => r.employeeId === employeeId));
      setDeclaredIds(new Set(pr.filter((r) => r.employeeId === employeeId).map((r) => r.activityId)));
      setLoaded(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [board, day, employeeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleActivity = async (activityId: number) => {
    setBusyKey(`a${activityId}`);
    setErr('');
    try {
      if (declaredIds.has(activityId)) {
        await boardPresence.withdrawPresence(activityId, employeeId, day);
      } else {
        // Declaring lifts a stale absence in the same transaction (migration
        // 0055) — the two are mutually exclusive by construction.
        await boardPresence.declarePresence({
          activityId,
          employeeId,
          date: day,
          status: 'declared',
          source: 'tap',
          declaredBy: employeeId,
        });
      }
      await load();
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  const toggleAbsent = async () => {
    setBusyKey('absent');
    setErr('');
    try {
      if (absent) await boardPresence.clearAbsence(employeeId, day);
      else await boardPresence.markAbsent({ employeeId, date: day, source: 'tap', declaredBy: employeeId });
      await load();
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <section className="sqe">
      <div className="sqe-head">
        <h3 className="sqe-title">{fmtDayLabel(day)}</h3>
        <button type="button" className="sqe-close" onClick={onClose} aria-label="Fermer">
          ✕
        </button>
      </div>

      {err && <p className="auth-err">Erreur : {err}</p>}
      {!loaded && !err && <p className="staff-empty">Chargement…</p>}

      {loaded && (
        <div className="presence-self-buttons">
          {myActivities.map((a) => (
            <button
              key={a.id}
              type="button"
              className={declaredIds.has(a.id) ? 'is-active' : ''}
              disabled={busyKey !== null || absent}
              onClick={() => void toggleActivity(a.id)}
            >
              {busyKey === `a${a.id}` ? '…' : a.label}
            </button>
          ))}
          <button
            type="button"
            className={`presence-self-absent${absent ? ' is-active' : ''}`}
            disabled={busyKey !== null}
            onClick={() => void toggleAbsent()}
          >
            {busyKey === 'absent' ? '…' : 'Absent·e'}
          </button>
        </div>
      )}
      {loaded && myActivities.length === 0 && (
        <p className="staff-empty">Aucune activité pour laquelle vous êtes habilité·e.</p>
      )}
    </section>
  );
}
